import React from 'react';
import {coursesListText} from '../coursesListText';
import PageTitle from '../../../../uiToolkit/PageTitle';
import SearchInput from '../../../../uiToolkit/Inputs/SearchInput';
import Button from '../../../../uiToolkit/Buttons/Button';
import {APP_PERMISSIONS} from '../../../../data/permissions';
import './styles.css';
import {logoTextWhite, coursesEmptyState} from '../../../../assets/images';
import ModalContainer from '../../../../uiToolkit/ModalContainer';
import SortPill from '../../../../uiToolkit/SortPill/SortPill';
import {CourseListItem} from '../../../../data/models/Course/Course';
import {WORKSPACE_COURSES_SORT_OPTIONS} from '../conf';
import NewCourseCard from '../components/CourseCard';
import {GRADABLE_COURSE_TYPE} from '../../../../data/staticValues/courseStructure';
import {Tour} from '../../../../modules/Tour';

interface CoursesListUIProps {
  onCreateNewCourse: () => void;
  onClose: () => void;
  openModal: () => void;
  showModal: boolean;
  onEdit: (courseId: string) => void;
  onDelete: (courseId: string) => void;
  onOpenFolders: (courseId: string) => void;
  isLoading: boolean;
  search: string;
  onCopyCourse: (courseId: string) => void;
  onSearchChange: (searchTerm: string) => void;
  permissions: string[];
  pagesCount: number;
  modalContent: React.ReactElement | null;
  loadNextPage: () => Promise<any>;
  courses: CourseListItem[];
  sortField: string;
  sortDirection: boolean;
  onSortChange: (field: string, direction: 'asc' | 'desc') => void;
  onPreview: (courseId: string) => void;
}

const CoursesListUI: React.FC<CoursesListUIProps> = ({
  onCreateNewCourse,
  onEdit,
  onDelete,
  onOpenFolders,
  isLoading,
  search,
  onCopyCourse,
  onSearchChange,
  permissions,
  onClose,
  showModal,
  modalContent,
  loadNextPage,
  courses,
  sortField,
  sortDirection,
  onSortChange,
  onPreview,
}) => {
  const direction = sortDirection ? 'asc' : 'desc';
  const observer = React.useRef<any>();
  const overscrollLock = React.useRef(false);

  const lastPostElementRef = (node: any) => {
    if (isLoading || overscrollLock.current) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        overscrollLock.current = true;
        loadNextPage().then(() => {
          overscrollLock.current = false;
        });
      }
    });

    if (node) observer.current.observe(node);
  };

  return (
    <div className="CoursesListContainer">
      <div className="CoursesListHeader">
        <PageTitle title={coursesListText.title} />
        <div className="CourseFiltersContainer">
          <SearchInput
            value={search}
            onChange={onSearchChange}
            className="Input"
          />
          {permissions.includes(APP_PERMISSIONS.ORG_CREATE_COURSES) && (
            <Button
              title={coursesListText.create}
              onClick={onCreateNewCourse}
              uiType="action"
              className="createCourseButton"
              icon="icon-log-out"
            />
          )}
        </div>
      </div>
      <Tour section="courses" />
      <div className="CoursesListFiltersContainer">
        <SortPill
          sortField={sortField}
          sortDirection={direction}
          onSetSort={onSortChange}
          options={WORKSPACE_COURSES_SORT_OPTIONS}
        />
      </div>
      {courses.length ? (
        <>
          <ul className="row CourseCardContainer">
            {courses.map((course, index) => {
              return (
                <NewCourseCard
                  key={`${course.id}-${index}`}
                  course={course}
                  courseId={course.id}
                  courseImage={course.image}
                  logoTextWhite={logoTextWhite}
                  courseTitle={course.title}
                  courseSubjecttitle={course.subjectTitle}
                  coursePublished={course.published}
                  courseIsPublic={course.isPublic}
                  courseGradable={
                    course.type.toString() === GRADABLE_COURSE_TYPE
                  }
                  courseAverageGrade={course.averageGrade}
                  courseEnrollNumber={course.enrollNumber}
                  courseLearners={course.enrollNumber}
                  onOpenFolders={() => onOpenFolders(course.id)}
                  onPreview={() => onPreview(course.id)}
                  onCopyCourse={() => onCopyCourse(course.id)}
                  onDelete={() => onDelete(course.id)}
                  onEdit={() => onEdit(course.id)}
                  permissions={permissions}
                />
              );
            })}
          </ul>
          <div ref={lastPostElementRef} />
        </>
      ) : (
        <div className="CoursesEmptyStateContainer">
          <img src={coursesEmptyState} />
          <p>
            {search !== ''
              ? coursesListText.noCoursesFound + search
              : coursesListText.emptyState1}
          </p>
          <p>
            {search !== ''
              ? coursesListText.noCoursesFound2
              : coursesListText.emptyState2}
          </p>
          {search === '' &&
          permissions.includes(APP_PERMISSIONS.ORG_CREATE_COURSES) ? (
            <Button
              onClick={onCreateNewCourse}
              title={coursesListText.create}
              uiType="action"
            />
          ) : null}
        </div>
      )}

      {showModal && (
        <ModalContainer onClose={onClose} hideCloseButton={true}>
          {modalContent || <React.Fragment />}
        </ModalContainer>
      )}
    </div>
  );
};

export default CoursesListUI;
